import { createFormatForDescription as cffd } from '../createFormatForDescription'
import explainAmount from '../explain/explainAmount'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import { MatchFlag, matchFlags, Flag } from '../flag'
import TrustSetInstructions from '../types/instructions/TrustSetInstructions'
import TransactionType from '../types/TransactionType'
import CurrencyAmount from '../types/util/CurrencyAmount'

/**
 * An annotation for TrustSet transactions.
 *
 * @property type - The transaction type.
 * @property additionalFlags - Any transaction-type specific flags.
 *
 * @property limitAmount - An explanation of the limitAmount.
 * @property qualityIn - An explanation of the qualityIn.
 * @property qualityOut - An explanation of the qualityOut.
 */
export interface TrustSetAnnotation {
  readonly type: TransactionType.TrustSet
  readonly additionalFlags: Flag[]

  readonly limitAmount: Explanation<CurrencyAmount>
  readonly qualityIn?: Explanation<number>
  readonly qualityOut?: Explanation<number>
}

enum FlagName {
  SetfAuth = 'tfSetfAuth',
  SetNoRipple = 'tfSetNoRipple',
  ClearNoRipple = 'tfClearNoRipple',
  SetFreeze = 'tfSetFreeze',
  ClearFreeze = 'tfClearFreeze',
}

enum FlagCode {
  SetfAuth = 0x00010000,
  SetNoRipple = 0x00020000,
  ClearNoRipple = 0x00040000,
  SetFreeze = 0x00100000,
  ClearFreeze = 0x00200000,
}

const extraFlags: MatchFlag[] = [
  {
    name: FlagName.SetfAuth,
    code: FlagCode.SetfAuth,
    label:
      'Authorize the other party to hold issuances from this account. (No effect unless using the asfRequireAuth AccountSet flag.) Cannot be unset.',
  },
  {
    name: FlagName.SetNoRipple,
    code: FlagCode.SetNoRipple,
    label:
      'Blocks rippling between two trustlines of the same currency, if this flag is set on both. (See No Ripple for details.) If the fix1578 amendment is enabled, a transaction that uses this flag and cannot enable NoRipple fails with the result code tecNO_PERMISSION. If the amendment is not enabled, the transaction can result in tesSUCCESS (making any other changes it can) even if it cannot enable NoRipple on the trust line.',
  },
  {
    name: FlagName.ClearNoRipple,
    code: FlagCode.ClearNoRipple,
    label: 'Clears the No-Rippling flag. (See NoRipple for details.)',
  },
  {
    name: FlagName.SetFreeze,
    code: FlagCode.SetFreeze,
    label: 'Freeze the trustline.',
  },
  {
    name: FlagName.ClearFreeze,
    code: FlagCode.ClearFreeze,
    label: 'Unfreeze the trustline.',
  },
]

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: TrustSetInstructions,
): TrustSetAnnotation {
  const additionalFlags = matchFlags(transactionInstructions.Flags, extraFlags)

  // TODO: no idea if these explanations are correct or if they make sense.
  const limitAmount = explain(
    transactionInstructions.LimitAmount,
    `Create or modify a trust line using ${explainAmount(
      transactionInstructions.LimitAmount,
    )}`,
  )

  const ratioNum = 1e9

  const qualityIn = explainOptional(
    transactionInstructions.QualityIn,
    (qIn) =>
      `Value incoming balances on this trust line at the ratio of ${cffd(
        (qIn / ratioNum).toString(),
        'qualityIn',
      )}`,
  )

  const qualityOut = explainOptional(
    transactionInstructions.QualityOut,
    (qOut) =>
      `Value outgoing balances on this trust line at the ratio of ${cffd(
        (qOut / ratioNum).toString(),
        'qualityOut',
      )}`,
  )

  return {
    type: TransactionType.TrustSet,
    additionalFlags,
    limitAmount,
    ...(qualityIn && { qualityIn }),
    ...(qualityOut && { qualityOut }),
  }
}
